import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getOnboardingStatus } from '../../services/authService';
import { CoinCelebration } from '../../components/CoinCelebration/CoinCelebration';
import EmailVerificationPrompt from '../../components/EmailVerificationPrompt/EmailVerificationPrompt';
import PendingCoinCard, { rarityName } from '../../components/PendingCoinCard/PendingCoinCard';
import styles from './QuizResult.module.css';

/**
 * Result of an unverified account's one quiz before email verification. The
 * quiz was scored normally; the coin it earned (if any) is held until the
 * email is verified, when the server grants it automatically.
 */
export default function PendingCoinResult({ result, fullTitle, missedReview, reportSection }) {
    const navigate = useNavigate();
    const { user, updateUserData } = useAuth();
    const { passed, score, totalQuestions, requiredToPass, pendingCoin } = result;
    const [revealed, setRevealed] = useState(!(passed && pendingCoin));
    const [continuing, setContinuing] = useState(false);
    const [continueError, setContinueError] = useState(null);

    // Verified in another tab or on another device: the coin was claimed
    // there, so pick up the new state and go see it.
    const handleVerifiedContinue = async () => {
        setContinuing(true);
        setContinueError(null);
        try {
            const status = await getOnboardingStatus();
            if (!status?.emailVerified) {
                setContinueError("We still can't see the verification. Open the link in the email we sent, then try again.");
                return;
            }
            updateUserData({
                emailVerified: true,
                ...(status.coinCount != null && { coinCount: status.coinCount }),
                ...(status.needsWatchlistOnboarding != null && { needsWatchlistOnboarding: status.needsWatchlistOnboarding }),
            });
            navigate(passed && pendingCoin ? '/me' : '/discover');
        } catch (err) {
            setContinueError(err?.message || 'Something went wrong. Please try again.');
        } finally {
            setContinuing(false);
        }
    };

    const context = { source_screen: 'quiz_result', ...(pendingCoin?.tmdbId && { media_id: pendingCoin.tmdbId, media_type: pendingCoin.kind }) };

    if (passed && pendingCoin) {
        return (
            <div className={styles.page}>
                {!revealed && (
                    <CoinCelebration
                        coinLabel={pendingCoin.coinLabel}
                        coinImageUrl={pendingCoin.coinImageUrl}
                        subtitle="🔒 Verify your email to claim it"
                        shareUrl={null}
                        onComplete={() => setRevealed(true)}
                    />
                )}
                <div className={styles.inner} style={{ opacity: revealed ? 1 : 0, transition: 'opacity 0.3s ease' }}>
                    <span className={styles.emoji}>🎉</span>
                    <h1 className={styles.headline}>You earned a {rarityName(pendingCoin.rarity)} coin!</h1>
                    {fullTitle && <p className={styles.subtitle}>{fullTitle}</p>}
                    <p className={styles.score}>{score} / {totalQuestions} correct</p>

                    <PendingCoinCard coin={pendingCoin} />

                    <div className={styles.hintBox}>
                        <p className={styles.hintText}>
                            Verify your email to claim this coin. It's saved for you — it lands in your collection the moment you verify.
                        </p>
                    </div>

                    <EmailVerificationPrompt
                        email={user?.email}
                        pendingCoin={pendingCoin}
                        onContinue={handleVerifiedContinue}
                        continuing={continuing}
                        error={continueError}
                        continueLabel="I've verified — see my coin"
                        context={context}
                    />

                    {missedReview}

                    <div className={styles.actions}>
                        <Link to="/discover" className={styles.btnSecondary}>Keep browsing</Link>
                    </div>
                    {reportSection}
                </div>
            </div>
        );
    }

    const needed = requiredToPass ?? 5;
    const missed = Math.max(0, needed - score);

    return (
        <div className={styles.page}>
            <div className={styles.inner}>
                <span className={styles.emoji}>😅</span>
                <h1 className={styles.headline}>Not quite!</h1>
                {fullTitle && <p className={styles.subtitle}>{fullTitle}</p>}
                <p className={styles.score}>{score} / {totalQuestions} correct</p>

                <div className={styles.hintBox}>
                    <p className={styles.hintText}>
                        You needed {needed} to pass.{' '}
                        {missed === 1
                            ? 'Just 1 more and you would have earned the coin!'
                            : `${missed} more correct and you would have earned it!`}
                    </p>
                </div>

                <EmailVerificationPrompt
                    email={user?.email}
                    variant="keep_playing"
                    onContinue={handleVerifiedContinue}
                    continuing={continuing}
                    error={continueError}
                    context={context}
                />

                {missedReview}

                <div className={styles.actions}>
                    <Link to="/discover" className={styles.btnSecondary}>Keep browsing</Link>
                </div>
                {reportSection}
            </div>
        </div>
    );
}
